import {
  useLazyQuery,
  usePaginationHelper,
  useQuery,
  useNavigate,
} from '@common/hooks';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Tooltip, Typography } from 'antd';
import {
  buildOptionsFromData,
  ButtonGroup,
  Currency,
  DateRangeFilter,
  ErrorHandler,
  FilteredTable,
  MultiSelectTableFilter,
  NumberRangeFilter,
  Portal,
  SelectColumnsDropdown,
  selectColumnsStore,
  TimeWithTimeZone,
} from '@common/components';
import { Spacing } from 'works/legacy/lib/style-helper';
import { stateDisplayMap } from '@scheduling/services';
import {
  SHIFT_OPENINGS_FILTER_FACILITIES,
  WORKFORCE_GROUP_FLAT_QUERY,
} from '@scheduling/graphql';
import { RoleType, RolesQuery } from '@clinician-profile/graphql';
import {
  SHIFT_GAP_OPENING_QUERY,
  SHIFT_OPENINGS_FILTER_UNITS,
  SHIFT_OPENINGS_FILTER_CREATORS,
} from '@scheduling/graphql/queries/shift-openings';
import {
  InputMaybe,
  OrderDirection,
  PaginatedShiftGapOpeningsQueryCgResponse,
  ShiftGapOpeningOrderField,
  ShiftOpeningsFilterCreatorsQueryCgResponse,
} from '@common/graphql/schema';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { OrganizationUnitType } from '@institutions/graphql';
import {
  ExportShiftsButton,
  IncentiveRateDisplay,
} from '@scheduling/components';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { buildDialogUrl, capitalizeFirstLetter } from '@common/services';

const { Text } = Typography;

type ShiftGapOpeningType = NonNullable<
  PaginatedShiftGapOpeningsQueryCgResponse['paginatedShiftGapOpenings']['shiftGapOpenings']
>[number];
type ShiftGapType = NonNullable<
  PaginatedShiftGapOpeningsQueryCgResponse['paginatedShiftGapOpenings']['shiftGapOpenings']
>[number]['shiftGap'];
type WorksShiftType = NonNullable<
  PaginatedShiftGapOpeningsQueryCgResponse['paginatedShiftGapOpenings']['shiftGapOpenings']
>[number]['worksShift'];
type ShiftCreatorType = NonNullable<
  ShiftOpeningsFilterCreatorsQueryCgResponse['shiftCreators']
>[number];

interface Props {
  triggerPortal: string;
}

export function ShiftOpeningsList({ triggerPortal }: Props) {
  const navigate = useNavigate();

  const localStorageKey = 'ShiftRequestTableV2';

  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: localStorageKey,
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'DESC',
      orderField: 'start',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      start: 'start',
      role: 'role',
      state: 'state',
    },
  });

  const [
    fetchOrganizationUnits,
    { data: organizationUnitData, error: organizationUnitError },
  ] = useLazyQuery(SHIFT_OPENINGS_FILTER_UNITS, {
    fetchPolicy: 'cache-first',
  });

  const [
    fetchOrganizationBusiness,
    { data: organizationBusinessData, error: organizationBusinessError },
  ] = useLazyQuery(SHIFT_OPENINGS_FILTER_FACILITIES, {
    fetchPolicy: 'cache-first',
  });

  const [fetchRoles, { data: rolesData, error: rolesError }] =
    useLazyQuery(RolesQuery);

  const [fetchWorkforceGroups, { data: wfgsData, error: wfgError }] =
    useLazyQuery(WORKFORCE_GROUP_FLAT_QUERY);

  const [
    fetchShiftCreatorsQuery,
    { data: requestorsData, error: requestorError },
  ] = useLazyQuery(SHIFT_OPENINGS_FILTER_CREATORS);

  const { loading, error, data } = useQuery(SHIFT_GAP_OPENING_QUERY, {
    variables: {
      ...paginationVariables,
      orderDirection: paginationVariables.orderDirection as
        | InputMaybe<OrderDirection>
        | undefined,
      orderField: paginationVariables.orderField as
        | InputMaybe<ShiftGapOpeningOrderField>
        | undefined,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      fetchRoles();
      fetchWorkforceGroups();
      fetchOrganizationUnits();
      fetchOrganizationBusiness();
    },
  });

  useEffect(() => {
    fetchShiftCreatorsQuery({
      variables: {
        filters: {
          unit: filters.unit,
          start: filters.start,
        },
      },
    });
  }, [filters.unit, filters.start, fetchShiftCreatorsQuery]);

  const facilityTags = [
    ...new Set(
      organizationBusinessData?.organizationBusinesses
        .map((business) => business.tags.map((tag) => tag))
        .flat()
    ),
  ];

  const filtersDataReady =
    !!organizationUnitData &&
    !!organizationBusinessData &&
    !!rolesData &&
    !!wfgsData;

  const pageData = data?.paginatedShiftGapOpenings;
  const shiftGapOpenings = pageData?.shiftGapOpenings ?? [];
  const total = pageData?.totalCount ?? null;

  // The keys of these columns are used in the shift export as the columns to be
  // exported, meaning that the keys used here must match one of the possible
  // keys in the backend.
  const allColumns = [
    {
      title: 'Start Time',
      dataIndex: 'shiftGap',
      key: 'start',
      render: (shiftGap: ShiftGapType) => {
        const startTime = moment
          .parseZone(shiftGap.startDatetime)
          .tz(shiftGap.timeZone);
        const date = startTime.format('MM/DD/yyyy');
        const time = startTime.format('HH:mm');
        return (
          <>
            <Text>{date}</Text>
            <br />
            <Text strong>{time}</Text>
          </>
        );
      },
      sorter: true,
      filterFormat: 'dateRange',
      filtered: !!filters.start || false,
      filteredValue: filters.start || null,
      filterDropdown: ({ setSelectedKeys, confirm }: FilterDropdownProps) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            defaultValue={filters.start?.map((date) =>
              moment(date, 'YYYY-MM-DD')
            )}
            autoFocus={true}
            onSearch={(value) => {
              setSelectedKeys(
                value
                  ? value.map((date) => moment(date).format?.('YYYY-MM-DD'))
                  : []
              );
              confirm();
            }}
          />
        </section>
      ),
    },
    {
      title: 'DOW / Period',
      key: 'timeOfDay',
      dataIndex: 'timeOfDay',
      render: (_: never, record: ShiftGapOpeningType) => (
        <>
          <Text>{record.shiftGap.dayOfWeek}</Text>
          <br />
          <Text type="secondary">
            {capitalizeFirstLetter(`${record.shiftGap.timeOfDay}`)}
          </Text>
        </>
      ),
      filtered: filters.timeOfDay || false,
      filteredValue: filters.timeOfDay || null,
      filters: [
        {
          text: 'Day',
          value: 'day',
        },
        {
          text: 'Night',
          value: 'night',
        },
      ],
    },
    {
      title: 'Length',
      dataIndex: 'shiftGap',
      key: 'duration',
      render: (shiftGap: ShiftGapType) => `${shiftGap.shiftDuration} hrs`,
      filterFormat: 'hoursRange',
      filtered: filters.duration || false,
      filteredValue: filters.duration || null,
      filterDropdown: ({ setSelectedKeys, confirm }: FilterDropdownProps) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(value) => {
              setSelectedKeys(value || []);
              confirm();
            }}
            min={1}
            max={24}
            defaultValue={filters.duration?.map((value) => Number(value))}
            enforceOrder
          />
        </section>
      ),
    },
    {
      title: 'Facility',
      dataIndex: ['shiftGap', 'organizationBusiness', 'name'],
      key: 'business',
      filtered: filters.business || false,
      filteredValue: filters.business || null,
      filters: buildOptionsFromData(
        organizationBusinessData?.organizationBusinesses || []
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Facility Tags',
      dataIndex: 'shiftGap',
      key: 'businessTags',
      render: (shiftGap: ShiftGapType) =>
        shiftGap.organizationBusiness.tags.join(', '),
      filtered: filters.businessTags || false,
      filteredValue: filters.businessTags || null,
      filters: facilityTags.map((tag) => ({
        text: tag,
        value: tag,
      })),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Unit / Cost Center',
      dataIndex: 'shiftGap',
      key: 'unit',
      render: (shiftGap: ShiftGapType) => (
        <>
          <Text>{shiftGap.organizationUnit?.name}</Text>
          <br />
          <Text type="secondary">{shiftGap.organizationUnit?.costCenter}</Text>
        </>
      ),
      filtered: filters.unit || false,
      filteredValue: filters.unit || null,
      filters: buildOptionsFromData(
        organizationUnitData?.organizationUnits || [],
        (unit: OrganizationUnitType) => ({
          text: `${unit.name} - ${unit.costCenter}`,
          value: unit.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Home Unit / Cost Center',
      dataIndex: 'worksShift',
      key: 'homeUnit',
      render: (worksShift: WorksShiftType) =>
        worksShift && worksShift.worker.homeUnit ? (
          <>
            <Text>{worksShift.worker.homeUnit.name}</Text>
            <br />
            <Text type="secondary">
              {worksShift.worker.homeUnit.costCenter}
            </Text>
          </>
        ) : (
          '--'
        ),
      filtered: filters.homeUnit || false,
      filteredValue: filters.homeUnit || null,
      filters: buildOptionsFromData(
        organizationUnitData?.organizationUnits || [],
        (unit: OrganizationUnitType) => ({
          text: `${unit.name} - ${unit.costCenter}`,
          value: unit.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: true,
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(
        rolesData?.roles || [],
        (role: RoleType) => ({
          text: role.abbreviation,
          value: role.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (_: never, record: ShiftGapOpeningType) => (
        <>{record.shiftGap.role?.abbreviation}</>
      ),
    },
    {
      title: 'Staff / Group',
      dataIndex: 'worksShift',
      key: 'workforceGroup',
      render: (worksShift: WorksShiftType) =>
        worksShift ? (
          <>
            <Text>{worksShift.worker.worksUser.name}</Text>
            <br />
            <Text type="secondary">
              {worksShift.worker.workforceGroup?.name}
            </Text>
          </>
        ) : (
          '--'
        ),
      filtered: filters.workforceGroup || false,
      filteredValue: filters.workforceGroup || null,
      filters: buildOptionsFromData(wfgsData?.workforceGroups || []),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Employee ID',
      dataIndex: 'worksShift',
      key: 'employeeId',
      render: (worksShift: WorksShiftType) =>
        worksShift?.worker?.employeeId || '--',
    },
    {
      title: 'Rate / Type',
      dataIndex: 'rate',
      key: 'rate',
      render: (_: never, record: ShiftGapOpeningType) => {
        if (!record.worksShift) {
          return <></>;
        }

        const worker = record.worksShift.worker;
        const worksShift = record.worksShift;

        if (worker.isInternal) {
          return (
            <>
              <IncentiveRateDisplay
                displayAsLumpSum={worksShift.displayAsLumpSum}
                formattedLumpSumRate={worksShift.formattedLumpSumIncentivePay}
                perHourRateCents={worksShift.perHourIncentivePay}
                tierLabel={worksShift.incentiveTierLabel}
              />
              <Text type="secondary">
                {capitalizeFirstLetter(worksShift.worksShiftType)}
              </Text>
            </>
          );
        }

        const billingRate =
          worker.allowLists?.find(
            (allowList) =>
              allowList.organizationUnit.id ===
              record?.shiftGap.organizationUnit.id
          )?.staffingRequestAssignment?.staffingRequest?.billingRegularRate ||
          worksShift.perHourIncentivePay / 100.0;

        return (
          <>
            <Currency value={billingRate} />
            /hr
            <br />
            <Text type="secondary">
              {capitalizeFirstLetter(worksShift.worksShiftType)}
            </Text>
          </>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: ShiftCreatorType) => <span>{createdBy.name}</span>,
      filtered: filters.createdBy || false,
      filteredValue: filters.createdBy || null,
      filters: buildOptionsFromData(requestorsData?.shiftCreators || []),
      filterDropdown: MultiSelectTableFilter,
      className: 'sentry-mask',
    },
    {
      title: 'Creator Type',
      dataIndex: 'createdBy',
      key: 'creatorType',
      render: (createdBy: ShiftCreatorType) => {
        return createdBy.systemUser ? 'Integration' : 'User';
      },
      filtered: filters.creatorType || false,
      filteredValue: filters.creatorType || null,
      filters: [
        {
          text: 'Integration',
          value: 'integration',
        },
        {
          text: 'User',
          value: 'user',
        },
      ],
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (state: string, record: ShiftGapOpeningType) => {
        const shiftState = record.worksShift ? record.worksShift.state : state;

        return (
          <>
            <Text
              style={{
                fontWeight: 500,
                color:
                  stateDisplayMap[shiftState as keyof typeof stateDisplayMap]
                    .color,
              }}
            >
              {
                stateDisplayMap[shiftState as keyof typeof stateDisplayMap]
                  .label
              }
            </Text>
            <br />
            <Text type="secondary">
              <TimeWithTimeZone
                date={`${record.stateChangedAt}`}
                variant="mid_format"
              />
            </Text>
          </>
        );
      },
      sorter: true,
      filtered: filters.state || false,
      filteredValue: filters.state || null,
      filters: [
        {
          text: 'Open',
          value: 'open',
        },
        {
          text: 'Expired',
          value: 'expired',
        },
        {
          text: 'Cancelled',
          value: 'cancelled',
        },
        {
          text: 'Pending Approval',
          value: 'claimed',
        },
        {
          text: 'Confirmed',
          value: 'confirmed',
        },
        {
          text: 'Approved',
          value: 'accepted',
        },
        {
          text: 'Rejected',
          value: 'rejected',
        },
      ],
    },
  ];

  const defaultColumnsKeys = !selectColumnsStore.get(localStorageKey)
    ? ([
        'start',
        'duration',
        'unit',
        'role',
        'workforceGroup',
        'rate',
        'createdBy',
        'state',
      ] as CheckboxValueType[])
    : selectColumnsStore.get(localStorageKey);

  const [selectedColumns, setSelectedColumns] = useState(defaultColumnsKeys);

  useEffect(() => {
    selectColumnsStore.set(localStorageKey, selectedColumns as string[]);
  }, [selectedColumns]);

  const handleColumnChange = (checkedValues: CheckboxValueType[]) => {
    setSelectedColumns(checkedValues);

    selectColumnsStore.set(localStorageKey, checkedValues as string[]);

    if (filters) {
      const newFilters = checkedValues.reduce(
        (acc, key) => {
          if (filters[key as string]) {
            acc[key as string] = filters[key as string];
          }
          return acc;
        },
        {} as Record<string, string[] | null>
      );

      filterChangeHandler(newFilters);
    }
  };

  const filteredColumns = allColumns.filter((col) =>
    selectedColumns.includes(`${col.key}`)
  );

  const exportMaxRowsAllowed =
    data?.paginatedShiftGapOpenings?.exportMaxRowsAllowed;
  const canExport = exportMaxRowsAllowed
    ? (total || 0) <= exportMaxRowsAllowed
    : true;

  function shortenNumericValue(value: number) {
    return Math.abs(value) > 999 ? `${(value / 1000).toFixed(0)}k` : `${value}`;
  }

  const exportDataToast = useMemo(() => {
    if (!canExport) {
      const exportLimitFormatted = shortenNumericValue(exportMaxRowsAllowed!);
      return `Shift Exports are limited to ${exportLimitFormatted} records. Please filter the list to reduce the number of records.`;
    }

    if (filteredColumns.length === 0) {
      return 'Please select columns to export.';
    }

    return null;
  }, [canExport, filteredColumns.length, exportMaxRowsAllowed]);

  const anyError =
    error ||
    wfgError ||
    rolesError ||
    requestorError ||
    organizationUnitError ||
    organizationBusinessError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="ShiftRequestList" />;
  }

  const buildShiftDetailsDrawerUrl = (shiftId: string, type: string) =>
    buildDialogUrl('shifts-request-details-drawer', {
      props: {
        shiftId,
        type,
      },
    });

  return (
    <div className="ShiftRequestListPage">
      <Portal
        targetId="wk-portal-target:org-shift-list-extra-actions"
        triggerRender={triggerPortal}
      >
        <ButtonGroup>
          <Tooltip placement="bottom" title={exportDataToast}>
            <ExportShiftsButton
              disabled={!canExport || filteredColumns.length === 0}
            />
          </Tooltip>
          <SelectColumnsDropdown
            allColumns={allColumns}
            onChange={handleColumnChange}
            defaultColumnsKeys={defaultColumnsKeys}
          />
        </ButtonGroup>
      </Portal>
      <FilteredTable
        rowKey="id"
        className="ShiftRequestsList"
        filtersDataReady={filtersDataReady}
        columns={filteredColumns}
        loading={loading}
        dataSource={filteredColumns.length > 0 ? shiftGapOpenings : []}
        pagination={{ ...paginationVariables, total }}
        onFilterChange={filterChangeHandler}
        localStorageFilterKey={localStorageFilterKey}
        onChange={tableChangeHandler}
        onRow={(record: ShiftGapOpeningType) => {
          return {
            onClick: () => {
              navigate(buildShiftDetailsDrawerUrl(record.shiftGap.id, 'feed'));
            },
          };
        }}
        filters={filters}
        locale={{
          emptyText:
            filteredColumns.length === 0 ? (
              <Empty description="Please select columns to populate the list" />
            ) : null,
        }}
      />

      <style jsx>{`
        :global(.ant-table-row) {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
